import React from 'react';
import { Info } from 'lucide-react';
import { TNaerasDashboardOffer, TNaerasPanel } from '@/data/types';
import EnergyChart from '@/pages/Calculate/components/shared/EnergyChart';
import HybridBarChart from '@/pages/Calculate/components/shared/HybridBarChart';
import PanelCard from './PanelCard';
import { COLORS, shortDate } from '../utils/format';

interface OfferPanelProps {
  panel: TNaerasPanel<TNaerasDashboardOffer> | undefined;
}

/** The hourly program of one station, in the shape EnergyChart draws. */
const hourlySeries = (values: number[] | undefined) =>
  Array.from({ length: 24 }, (_, hour) => ({
    id: hour,
    name: `Ώρα ${String(hour).padStart(2, '0')}`,
    value1: Number(values?.[hour] ?? 0),
    value2: 0,
  }));

/**
 * §2.1 — the offer in force today, drawn with the bid screen's own charts.
 *
 * It comes from the dashboard rather than `GET /naeras/bids/<today>`: a bid
 * row exists only for days someone computed in the bids screen, while an
 * offer is in force every day.
 */
export const OfferPanel: React.FC<OfferPanelProps> = ({ panel }) => (
  <PanelCard
    title="Προσφορά Σήμερα"
    subtitle="Η προσφορά που ισχύει για σήμερα"
    panel={panel}
    aside={(offer) =>
      offer.source === 'job' ? (
        <span className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
          <Info className="h-3.5 w-3.5" />
          δεν οριστικοποιήθηκε — ισχύει η προσφορά του συστήματος
        </span>
      ) : (
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
          Οριστική
        </span>
      )
    }
  >
    {(offer) => {
      const day = shortDate(offer.target_date);

      return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <EnergyChart
            data={hourlySeries(offer.program.Ano_Proespera)}
            title="Πρόγραμμα Προεσπέρας"
            subtitle={`Ωριαία προσφορά (MW), ${day}`}
            series={[{ key: 'value1', name: 'Προεσπέρα', color: COLORS.hydro, gradient: '' }]}
            height={240}
          />
          <EnergyChart
            data={hourlySeries(offer.program.Kato_Proespera)}
            title="Πρόγραμμα Κάτω Προεσπέρας"
            subtitle={`Ωριαία προσφορά (MW), ${day}`}
            series={[{ key: 'value1', name: 'Κάτω Προεσπέρα', color: '#1d4ed8', gradient: '' }]}
            height={240}
          />
          <div className="lg:col-span-2">
            <HybridBarChart
              first12hMwh={Number(offer.program.Hybrid_12h?.[0] ?? 0)}
              second12hMwh={Number(offer.program.Hybrid_12h?.[1] ?? 0)}
              dayLabel={day}
              height={240}
              color={COLORS.hybrid}
            />
          </div>
        </div>
      );
    }}
  </PanelCard>
);

export default OfferPanel;
